import { Button, CalendarIcon, CheckIcon, IconSize, ReloadIcon, Tooltip } from '@protoss/ui';
import * as React from 'react';
import { useNavigate } from 'react-router-dom';
import { AppNav } from '../components/AppNav';
import { AppShell, MainContentShell, type RightRailItem } from '../components/shell';
import { useLayoutConfig } from '../contexts/LayoutContext';
import { useWorkspace } from '../contexts/WorkspaceContext';
import { CalendarSettingsPane } from '../features/meetings/components/CalendarSettingsPane';
import { MeetingsPanel } from '../features/meetings/components/MeetingsPanel';
import { useCalendarSync } from '../features/meetings/hooks/useCalendarSync';
import { useWorkspaceAdmin } from '../hooks/useWorkspaceAdmin';

/**
 * MeetingsPage - workspace-level calendar meetings view for the product module.
 *
 * Accessible from the Meetings nav item in AppNav (visible when Google Calendar
 * is configured at the workspace admin level).
 *
 * Shows upcoming calendar events synced from Google Calendar. The right rail
 * panel lets each user connect their personal Google Calendar account.
 */
function MeetingsPageContent() {
  const navigate = useNavigate();
  const { workspace, isLoadingWorkspace } = useWorkspace();
  const isWorkspaceAdmin = useWorkspaceAdmin(workspace);
  const { syncing, syncDone, syncError, sync, reset: resetSync } = useCalendarSync();
  const [refreshKey, setRefreshKey] = React.useState(0);

  // No context pane on this page; reset volatile layout state on unmount.
  useLayoutConfig({ showContextPane: false });

  const handleSync = React.useCallback(async () => {
    resetSync();
    await sync();
    setRefreshKey((k) => k + 1);
  }, [sync, resetSync]);

  if (isLoadingWorkspace) {
    return null;
  }

  const syncButton = (
    <Tooltip.Simple label="Sync your calendar now">
      <Button
        type="button"
        variant="primary"
        size="sm"
        onClick={() => void handleSync()}
        disabled={syncing}
        aria-label="Sync"
      >
        {syncing ? (
          <ReloadIcon
            width={IconSize.sm}
            height={IconSize.sm}
            aria-hidden="true"
            className="meetings-page__sync-spinner"
          />
        ) : syncDone ? (
          <CheckIcon width={IconSize.sm} height={IconSize.sm} aria-hidden="true" />
        ) : (
          'Sync'
        )}
      </Button>
    </Tooltip.Simple>
  );

  const leftNav = (
    <AppNav
      selected="meetings"
      isWorkspaceAdmin={isWorkspaceAdmin}
      onAdministrationClick={() => navigate('/administration')}
    />
  );

  const rightRailItems: RightRailItem[] = [
    {
      id: 'calendar-settings',
      label: 'Calendar Settings',
      icon: <CalendarIcon width={IconSize.md} height={IconSize.md} aria-hidden="true" />,
      content: <CalendarSettingsPane />,
    },
  ];

  const main = (
    <MainContentShell title="Meetings" actions={syncButton}>
      <MeetingsPanel refreshKey={refreshKey} syncError={syncError} onResetSyncError={resetSync} />
    </MainContentShell>
  );

  return <AppShell leftNav={leftNav} main={main} rightRailItems={rightRailItems} />;
}

export function MeetingsPage() {
  return <MeetingsPageContent />;
}
